import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// UI Store 상태 타입
interface UIState {
  // 사이드바 상태
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;

  // 모달 상태
  isModalOpen: boolean;
  modalContent: React.ReactNode | null;

  // 로딩 상태
  isGlobalLoading: boolean;
  loadingMessage: string;

  // 토스트/알림
  toasts: Toast[];

  // POS 시리얼
  posSerial: string | null;

  // 헤더 탭
  headerTabs: { id: string; label: string }[] | null;
  headerActiveTab: string | null;
  headerOnTabChange: ((id: string) => void) | null;

  // 테마
  theme: 'light' | 'dark';

  // 액션
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setSidebarCollapsed: (isCollapsed: boolean) => void;
  setPosSerial: (serial: string | null) => void;
  setHeaderTabs: (tabs: { id: string; label: string }[] | null, activeTab?: string | null, onTabChange?: ((id: string) => void) | null) => void;

  openModal: (content: React.ReactNode) => void;
  closeModal: () => void;

  setGlobalLoading: (isLoading: boolean, message?: string) => void;

  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;

  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

/**
 * UI 상태 관리 Zustand Store
 * - 사이드바 상태
 * - 모달 상태
 * - 로딩 상태
 * - 토스트 알림
 * - 테마
 */
const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // ==================== 상태 ====================
      isSidebarOpen: true,
      isSidebarCollapsed: true,
      posSerial: localStorage.getItem('pos_serial') || null,
      headerTabs: null,
      headerActiveTab: null,
      headerOnTabChange: null,
      isModalOpen: false,
      modalContent: null,
      isGlobalLoading: false,
      loadingMessage: '',
      toasts: [],
      theme: 'light',

      // ==================== 사이드바 액션 ====================
      toggleSidebar: () => {
        set(state => ({ isSidebarOpen: !state.isSidebarOpen }));
      },

      setSidebarOpen: (isOpen: boolean) => {
        set({ isSidebarOpen: isOpen });
      },

      setSidebarCollapsed: (isCollapsed: boolean) => {
        set({ isSidebarCollapsed: isCollapsed });
      },

      setPosSerial: (serial: string | null) => {
        if (serial) {
          localStorage.setItem('pos_serial', serial);
        } else {
          localStorage.removeItem('pos_serial');
        }
        set({ posSerial: serial });
      },

      setHeaderTabs: (tabs, activeTab = null, onTabChange = null) => {
        set({ headerTabs: tabs, headerActiveTab: activeTab, headerOnTabChange: onTabChange });
      },

      // ==================== 모달 액션 ====================
      openModal: (content: React.ReactNode) => {
        set({ isModalOpen: true, modalContent: content });
      },

      closeModal: () => {
        set({ isModalOpen: false, modalContent: null });
      },

      // ==================== 로딩 액션 ====================
      setGlobalLoading: (isLoading: boolean, message: string = '') => {
        set({ isGlobalLoading: isLoading, loadingMessage: message });
      },

      // ==================== 토스트 액션 ====================
      addToast: (toast: Omit<Toast, 'id'>) => {
        const id = Date.now().toString();
        const newToast: Toast = { ...toast, id };

        set(state => ({ toasts: [...state.toasts, newToast] }));

        // 자동 제거
        const duration = toast.duration || 3000;
        setTimeout(() => {
          get().removeToast(id);
        }, duration);
      },

      removeToast: (id: string) => {
        set(state => ({
          toasts: state.toasts.filter(toast => toast.id !== id),
        }));
      },

      // ==================== 테마 액션 ====================
      setTheme: (theme: 'light' | 'dark') => {
        set({ theme });
        document.documentElement.setAttribute('data-theme', theme);
      },

      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        document.documentElement.setAttribute('data-theme', newTheme);
      },
    }),
    {
      name: 'ui-store',
      enabled: import.meta.env.DEV,
    }
  )
);

export default useUIStore;
